import clsx from "clsx";
import React from "react";

import {
  CLASSES,
  DEFAULT_SIDEBAR,
  TOOL_TYPE,
  arrayToMap,
  capitalizeString,
} from "@excalidraw/common";

import {
  mutateElement,
  getElementAbsoluteCoords,
  isImageElement,
} from "@excalidraw/element";

import { showSelectedShapeActions } from "@excalidraw/element";

import { ShapeCache } from "@excalidraw/element";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { actionClearCanvas, actionToggleStats } from "../actions";
import { trackEvent } from "../analytics";
import { isHandToolActive } from "../appState";
import { TunnelsContext, useInitializeTunnels } from "../context/tunnels";
import { UIAppStateContext } from "../context/ui-appState";
import { useAtom, useAtomValue, useSetAtom } from "../editor-jotai";

import { t } from "../i18n";
import { calculateScrollCenter } from "../scene";
import { centerScrollOn } from "../scene/scroll";

import {
  SelectedShapeActions,
  ShapesSwitcher,
  CompactShapeActions,
} from "./Actions";
import { LoadingMessage } from "./LoadingMessage";
import { MobileMenu } from "./MobileMenu";
import { PasteChartDialog } from "./PasteChartDialog";
import { Section } from "./Section";
import Stack from "./Stack";
import { UserList } from "./UserList";
import { PenModeButton } from "./PenModeButton";
import Footer from "./footer/Footer";
import { isSidebarDockedAtom } from "./Sidebar/Sidebar";
import MainMenu from "./main-menu/MainMenu";
import {
  ActiveConfirmDialog,
  activeConfirmDialogAtom,
} from "./ActiveConfirmDialog";
import { useEditorInterface, useStylesPanelMode } from "./App";
import { OverwriteConfirmDialog } from "./OverwriteConfirm/OverwriteConfirm";
import { sidebarRightIcon, TrashIcon, ImageIcon } from "./icons";
import { DefaultSidebar } from "./DefaultSidebar";
import { TTDDialog } from "./TTDDialog/TTDDialog";
import { Stats } from "./Stats";
import ElementLinkDialog from "./ElementLinkDialog";
import { ErrorDialog } from "./ErrorDialog";
import { EyeDropper, activeEyeDropperAtom } from "./EyeDropper";
import { FixedSideContainer } from "./FixedSideContainer";
import { HandButton } from "./HandButton";
import { ToolButton } from "./ToolButton";
// import { SlCursorMove } from "react-icons/sl";
import { HelpDialog } from "./HelpDialog";
import { HintViewer } from "./HintViewer";
import { ImageExportDialog } from "./ImageExportDialog";
import { Island } from "./Island";
import { JSONExportDialog } from "./JSONExportDialog";
import { LaserPointerButton } from "./LaserPointerButton";

import "./LayerUI.scss";
import "./Toolbar.scss";

import { ExitZenModeButton, UndoRedoActions, ZoomActions } from "./Actions";

import type { ActionManager } from "../actions/manager";

import type { Language } from "../i18n";
import type {
  AppProps,
  AppState,
  ExcalidrawProps,
  BinaryFiles,
  UIAppState,
  AppClassProperties,
} from "../types";

interface LayerUIProps {
  actionManager: ActionManager;
  appState: UIAppState;
  files: BinaryFiles;
  canvas: HTMLCanvasElement;
  setAppState: React.Component<any, AppState>["setState"];
  elements: readonly NonDeletedExcalidrawElement[];
  onHandToolToggle: () => void;
  onPenModeToggle: AppClassProperties["togglePenMode"];
  showExitZenModeBtn: boolean;
  langCode: Language["code"];
  renderTopLeftUI?: ExcalidrawProps["renderTopLeftUI"];
  renderTopRightUI?: ExcalidrawProps["renderTopRightUI"];
  renderCustomStats?: ExcalidrawProps["renderCustomStats"];
  UIOptions: AppProps["UIOptions"];
  onExportImage: AppClassProperties["onExportImage"];
  renderWelcomeScreen: boolean;
  children?: React.ReactNode;
  app: AppClassProperties;
  isCollaborating: boolean;
  generateLinkForSelection?: AppProps["generateLinkForSelection"];
}

const DefaultMainMenu: React.FC<{
  UIOptions: AppProps["UIOptions"];
}> = ({ UIOptions }) => {
  return (
    <MainMenu __fallback>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      {/* FIXME we should to test for this inside the item itself */}
      {UIOptions.canvasActions.export && <MainMenu.DefaultItems.Export />}
      {/* FIXME we should to test for this inside the item itself */}
      {UIOptions.canvasActions.saveAsImage && (
        <MainMenu.DefaultItems.SaveAsImage />
      )}
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.Group title="Excalidraw links">
        <MainMenu.DefaultItems.Socials />
      </MainMenu.Group>
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme />
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.DefaultItems.ChangeFontFamily />
    </MainMenu>
  );
};

const DefaultOverwriteConfirmDialog = () => {
  return (
    <OverwriteConfirmDialog __fallback>
      <OverwriteConfirmDialog.Actions.SaveToDisk />
      <OverwriteConfirmDialog.Actions.ExportToImage />
    </OverwriteConfirmDialog>
  );
};

const LayerUI = ({
  actionManager,
  appState,
  files,
  setAppState,
  elements,
  canvas,
  onHandToolToggle,
  onPenModeToggle,
  showExitZenModeBtn,
  renderTopLeftUI,
  renderTopRightUI,
  renderCustomStats,
  UIOptions,
  onExportImage,
  renderWelcomeScreen,
  children,
  app,
  isCollaborating,
  generateLinkForSelection,
}: LayerUIProps) => {
  const editorInterface = useEditorInterface();
  const stylesPanelMode = useStylesPanelMode();
  const isCompactStylesPanel = stylesPanelMode === "compact";
  const tunnels = useInitializeTunnels();

  const setActiveConfirmDialog = useSetAtom(activeConfirmDialogAtom);

  const spacing = isCompactStylesPanel
    ? {
        menuTopGap: 4,
        toolbarColGap: 4,
        toolbarRowGap: 1,
        toolbarInnerRowGap: 2,
        islandPadding: 1,
        collabMarginLeft: 8,
      }
    : {
        menuTopGap: 6,
        toolbarColGap: 4,
        toolbarRowGap: 1,
        toolbarInnerRowGap: 2,
        islandPadding: 1,
        collabMarginLeft: 8,
      };

  const TunnelsJotaiProvider = tunnels.tunnelsJotai.Provider;

  const [eyeDropperState, setEyeDropperState] = useAtom(activeEyeDropperAtom);

  const onHome = () => {
    const firstElement = elements[0];
    if (firstElement) {
      const [x1, y1, x2, y2] = getElementAbsoluteCoords(
        firstElement,
        app.scene.getNonDeletedElementsMap(),
      );
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      setAppState(
        centerScrollOn({
          scenePoint: { x: centerX, y: centerY },
          viewportDimensions: {
            width: appState.width,
            height: appState.height,
          },
          zoom: appState.zoom,
        }),
      );
    }
  };

  const renderJSONExportDialog = () => {
    if (!UIOptions.canvasActions.export) {
      return null;
    }

    return (
      <JSONExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        exportOpts={UIOptions.canvasActions.export}
        canvas={canvas}
        setAppState={setAppState}
      />
    );
  };

  const renderImageExportDialog = () => {
    if (
      !UIOptions.canvasActions.saveAsImage ||
      appState.openDialog?.name !== "imageExport"
    ) {
      return null;
    }

    return (
      <ImageExportDialog
        elements={elements}
        appState={appState}
        files={files}
        actionManager={actionManager}
        onExportImage={onExportImage}
        onCloseRequest={() => setAppState({ openDialog: null })}
        name={app.getName()}
      />
    );
  };

  const renderCanvasActions = () => (
    <div style={{ position: "relative" }}>
      {/* wrapping to Fragment stops React from occasionally complaining
                about identical Keys */}
      <tunnels.MainMenuTunnel.Out />
      {renderWelcomeScreen && <tunnels.WelcomeScreenMenuHintTunnel.Out />}
    </div>
  );

  const renderSelectedShapeActions = () => {
    const isCompactMode = isCompactStylesPanel;

    return (
      <Section
        heading="selectedShapeActions"
        className={clsx("selected-shape-actions zen-mode-transition", {
          "transition-left": appState.zenModeEnabled,
        })}
      >
        {isCompactMode ? (
          <Island
            className={clsx("compact-shape-actions-island")}
            padding={0}
            style={{
              // we want to make sure this doesn't overflow so subtracting the
              // approximate height of hamburgerMenu + footer
              maxHeight: `${appState.height - 166}px`,
            }}
          >
            <CompactShapeActions
              appState={appState}
              elementsMap={app.scene.getNonDeletedElementsMap()}
              renderAction={actionManager.renderAction}
              app={app}
              setAppState={setAppState}
            />
          </Island>
        ) : (
          <Island
            className={CLASSES.SHAPE_ACTIONS_MENU}
            padding={2}
            style={{
              // we want to make sure this doesn't overflow so subtracting the
              // approximate height of hamburgerMenu + footer
              maxHeight: `${appState.height - 166}px`,
            }}
          >
            <SelectedShapeActions
              appState={appState}
              elementsMap={app.scene.getNonDeletedElementsMap()}
              renderAction={actionManager.renderAction}
              app={app}
            />
          </Island>
        )}
      </Section>
    );
  };

  const renderFixedSideContainer = () => {
    const selectedElements = elements.filter(
      (element) => appState.selectedElementIds[element.id],
    );
    const isSingleSelectedImage =
      selectedElements.length === 1 && isImageElement(selectedElements[0]);

    // COMMENTED DOWN: Disable the SelectedShapeActions panel entirely.
    // This panel shows Stroke/Background/Opacity/etc. when selecting shapes.
    // Keeping the original conditions here for easy re-enable if needed.
    // const shouldRenderSelectedShapeActions =
    //   showSelectedShapeActions(appState, elements) &&
    //   (appState.activeTool.type !== "freedraw" ||
    //     // if there is an active selection, always allow the panel
    //     Object.values(appState.selectedElementIds).some(Boolean)) &&
    //   // For images we show a floating toolbar near the selection instead of
    //   // rendering the left panel options (Edges/Opacity/Actions).
    //   !isSingleSelectedImage;
    const shouldRenderSelectedShapeActions = false;

    const shouldShowStats =
      appState.stats.open &&
      !appState.zenModeEnabled &&
      !appState.viewModeEnabled &&
      appState.openDialog?.name !== "elementLinkSelector";

    return (
      <FixedSideContainer side="top">
        <div className="App-menu App-menu_top">
          <div>
            <Stack.Col
              gap={spacing.menuTopGap}
              className={clsx("App-menu_top__left custom_menu")}
            >
              {renderCanvasActions()}
              <div
                className={clsx("selected-shape-actions-container", {
                  "selected-shape-actions-container--compact":
                    isCompactStylesPanel,
                })}
              >
                {shouldRenderSelectedShapeActions &&
                  renderSelectedShapeActions()}
              </div>
            </Stack.Col>
          </div>

          <div className="App-menu App-menu_top">
            {/* Zoom In and Out */}
            <div
              className={clsx(
                "layer-ui__wrapper__footer-left zen-mode-transition responsive-header-actions",
                {
                  "layer-ui__wrapper__footer-left--transition-left":
                    appState.zenModeEnabled,
                },
              )}
            >
              <Stack.Col gap={2}>
                <Section heading="canvasActions" className="size_box">
                  <ZoomActions
                    renderAction={actionManager.renderAction}
                    zoom={appState.zoom}
                    onHome={onHome}
                  />

                  {/* {!appState.viewModeEnabled && (
                  <UndoRedoActions
                    renderAction={actionManager.renderAction}
                    className={clsx("zen-mode-transition", {
                      "layer-ui__wrapper__footer-left--transition-bottom":
                        appState.zenModeEnabled,
                    })}
                  />
                )} */}
                </Section>
              </Stack.Col>
            </div>
            {!appState.viewModeEnabled &&
              appState.openDialog?.name !== "elementLinkSelector" && (
                <Section heading="shapes" className="shapes-section">
                  {(heading: React.ReactNode) => (
                    <div style={{ position: "relative" }}>
                      {renderWelcomeScreen && (
                        <tunnels.WelcomeScreenToolbarHintTunnel.Out />
                      )}
                      <Stack.Col gap={spacing.toolbarColGap} align="start">
                        <Stack.Row
                          gap={spacing.toolbarRowGap}
                          className={clsx("App-toolbar-container", {
                            "zen-mode": appState.zenModeEnabled,
                          })}
                        >
                          <Island
                            padding={spacing.islandPadding}
                            className={clsx("App-toolbar", {
                              "zen-mode": appState.zenModeEnabled,
                              "App-toolbar--compact": isCompactStylesPanel,
                            })}
                          >
                            <HintViewer
                              appState={appState}
                              isMobile={editorInterface.formFactor === "phone"}
                              editorInterface={editorInterface}
                              app={app}
                            />
                            {heading}
                            <Stack.Row gap={spacing.toolbarInnerRowGap}>
                              <PenModeButton
                                zenModeEnabled={appState.zenModeEnabled}
                                checked={appState.penMode}
                                onChange={() => onPenModeToggle(null)}
                                title={t("toolBar.penMode")}
                                penDetected={appState.penDetected}
                              />
                              {/* Lock button hidden; tool-lock is enabled by default */}

                              <div className="App-toolbar__divider" />
                              <ShapesSwitcher
                                setAppState={setAppState}
                                activeTool={appState.activeTool}
                                UIOptions={UIOptions}
                                app={app}
                                renderAfterToolButtons={() => (
                                  <>
                                    <HandButton
                                      checked={isHandToolActive(appState)}
                                      onChange={() => onHandToolToggle()}
                                      title={t("toolBar.hand")}
                                      isMobile
                                    />
                                    {actionManager.isActionEnabled(
                                      actionClearCanvas,
                                    ) && (
                                      <ToolButton
                                        type="button"
                                        icon={TrashIcon}
                                        aria-label={t("buttons.clearReset")}
                                        title={t("buttons.clearReset")}
                                        onClick={() =>
                                          setActiveConfirmDialog("clearCanvas")
                                        }
                                      />
                                    )}

                                    {UIOptions.tools?.image !== false && (
                                      <ToolButton
                                        className={clsx({
                                          active:
                                            appState.activeTool.type ===
                                            "image",
                                        })}
                                        type="radio"
                                        icon={ImageIcon}
                                        checked={
                                          appState.activeTool.type === "image"
                                        }
                                        name="editor-current-shape"
                                        title={`${capitalizeString(
                                          t("toolBar.image"),
                                        )}`}
                                        aria-label={capitalizeString(
                                          t("toolBar.image"),
                                        )}
                                        data-testid="toolbar-image"
                                        onClick={(event) => {
                                          // Re-open file picker on repeated click after cancel.
                                          if (
                                            appState.activeTool.type === "image"
                                          ) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            app.setActiveTool({
                                              type: "image",
                                            });
                                          }
                                        }}
                                        onChange={() =>
                                          app.setActiveTool({ type: "image" })
                                        }
                                      />
                                    )}
                                  </>
                                )}
                              />
                            </Stack.Row>
                          </Island>
                          {isCollaborating && (
                            <Island
                              style={{
                                marginLeft: spacing.collabMarginLeft,
                                alignSelf: "center",
                                height: "fit-content",
                              }}
                            >
                              <LaserPointerButton
                                title={t("toolBar.laser")}
                                checked={
                                  appState.activeTool.type === TOOL_TYPE.laser
                                }
                                onChange={() =>
                                  app.setActiveTool({ type: TOOL_TYPE.laser })
                                }
                                isMobile
                              />
                            </Island>
                          )}
                        </Stack.Row>
                      </Stack.Col>
                    </div>
                  )}
                </Section>
              )}

            {/* Undo/Redo */}
            {!appState.viewModeEnabled && (
              <UndoRedoActions
                renderAction={actionManager.renderAction}
                className={clsx(
                  "zen-mode-transition responsive-header-actions",
                  {
                    "layer-ui__wrapper__footer-left--transition-bottom":
                      appState.zenModeEnabled,
                  },
                )}
              />
            )}
            {/*  */}

            <div
              className={clsx(
                "layer-ui__wrapper__top-right zen-mode-transition",
                {
                  "transition-right": appState.zenModeEnabled,
                  "layer-ui__wrapper__top-right--compact": isCompactStylesPanel,
                },
              )}
            >
              {renderTopRightUI?.(
                editorInterface.formFactor === "phone",
                appState,
              )}
            </div>

            {/* <div
            className={clsx(
              "layer-ui__wrapper__top-right zen-mode-transition",
              {
                "transition-right": appState.zenModeEnabled,
                "layer-ui__wrapper__top-right--compact": isCompactStylesPanel,
              },
            )}
          >
            {appState.collaborators.size > 0 && (
              <UserList
                collaborators={appState.collaborators}
                userToFollow={appState.userToFollow?.socketId || null}
              />
            )}
            {renderTopRightUI?.(
              editorInterface.formFactor === "phone",
              appState,
            )}
            {!appState.viewModeEnabled &&
              appState.openDialog?.name !== "elementLinkSelector" &&
              // hide button when sidebar docked
              (!isSidebarDocked ||
                appState.openSidebar?.name !== DEFAULT_SIDEBAR.name) && (
                <tunnels.DefaultSidebarTriggerTunnel.Out />
              )}
            {shouldShowStats && (
              <Stats
                app={app}
                onClose={() => {
                  actionManager.executeAction(actionToggleStats);
                }}
                renderCustomStats={renderCustomStats}
              />
            )}
          </div> */}
          </div>
        </div>
      </FixedSideContainer>
    );
  };

  const renderSidebars = () => {
    return (
      <DefaultSidebar
        __fallback
        onDock={(docked) => {
          trackEvent(
            "sidebar",
            `toggleDock (${docked ? "dock" : "undock"})`,
            `(${
              editorInterface.formFactor === "phone" ? "mobile" : "desktop"
            })`,
          );
        }}
      />
    );
  };

  const isSidebarDocked = useAtomValue(isSidebarDockedAtom);

  const layerUIJSX = (
    <>
      {/* ------------------------- tunneled UI ---------------------------- */}
      {/* make sure we render host app components first so that we can detect
          them first on initial render to optimize layout shift */}
      {children}
      {/* render component fallbacks. Can be rendered anywhere as they'll be
          tunneled away. We only render tunneled components that actually
        have defaults when host do not render anything. */}
      <DefaultMainMenu UIOptions={UIOptions} />
      <DefaultOverwriteConfirmDialog />
      {appState.openDialog?.name === "ttd" && <TTDDialog __fallback />}
      {/* ------------------------------------------------------------------ */}

      {appState.isLoading && <LoadingMessage delay={250} />}
      {appState.errorMessage && (
        <ErrorDialog onClose={() => setAppState({ errorMessage: null })}>
          {appState.errorMessage}
        </ErrorDialog>
      )}
      {eyeDropperState && editorInterface.formFactor !== "phone" && (
        <EyeDropper
          colorPickerType={eyeDropperState.colorPickerType}
          onCancel={() => {
            setEyeDropperState(null);
          }}
          onChange={(colorPickerType, color, selectedElements, { altKey }) => {
            if (
              colorPickerType !== "elementBackground" &&
              colorPickerType !== "elementStroke"
            ) {
              return;
            }

            if (selectedElements.length) {
              for (const element of selectedElements) {
                mutateElement(element, arrayToMap(elements), {
                  [altKey && eyeDropperState.swapPreviewOnAlt
                    ? colorPickerType === "elementBackground"
                      ? "strokeColor"
                      : "backgroundColor"
                    : colorPickerType === "elementBackground"
                    ? "backgroundColor"
                    : "strokeColor"]: color,
                });
                ShapeCache.delete(element);
              }
              app.scene.triggerUpdate();
            } else if (colorPickerType === "elementBackground") {
              setAppState({
                currentItemBackgroundColor: color,
              });
            } else {
              setAppState({ currentItemStrokeColor: color });
            }
          }}
          onSelect={(color, event) => {
            setEyeDropperState((state) => {
              return state?.keepOpenOnAlt && event.altKey ? state : null;
            });
            eyeDropperState?.onSelect?.(color, event);
          }}
        />
      )}
      {appState.openDialog?.name === "help" && (
        <HelpDialog
          onClose={() => {
            setAppState({ openDialog: null });
          }}
        />
      )}
      <ActiveConfirmDialog />
      {appState.openDialog?.name === "elementLinkSelector" && (
        <ElementLinkDialog
          sourceElementId={appState.openDialog.sourceElementId}
          onClose={() => {
            setAppState({
              openDialog: null,
            });
          }}
          scene={app.scene}
          appState={appState}
          generateLinkForSelection={generateLinkForSelection}
        />
      )}
      <tunnels.OverwriteConfirmDialogTunnel.Out />
      {renderImageExportDialog()}
      {renderJSONExportDialog()}
      {appState.pasteDialog.shown && (
        <PasteChartDialog
          setAppState={setAppState}
          appState={appState}
          onClose={() =>
            setAppState({
              pasteDialog: { shown: false, data: null },
            })
          }
        />
      )}
      {editorInterface.formFactor === "phone" && (
        <MobileMenu
          app={app}
          appState={appState}
          elements={elements}
          actionManager={actionManager}
          renderJSONExportDialog={renderJSONExportDialog}
          renderImageExportDialog={renderImageExportDialog}
          setAppState={setAppState}
          onHandToolToggle={onHandToolToggle}
          onPenModeToggle={onPenModeToggle}
          renderTopLeftUI={renderTopLeftUI}
          renderTopRightUI={renderTopRightUI}
          renderSidebars={renderSidebars}
          renderWelcomeScreen={renderWelcomeScreen}
          UIOptions={UIOptions}
        />
      )}
      {editorInterface.formFactor !== "phone" && (
        <>
          <div
            className="layer-ui__wrapper"
            style={
              appState.openSidebar &&
              isSidebarDocked &&
              editorInterface.canFitSidebar
                ? { width: `calc(100% - var(--right-sidebar-width))` }
                : {}
            }
          >
            {renderWelcomeScreen && <tunnels.WelcomeScreenCenterTunnel.Out />}
            {renderFixedSideContainer()}
            <Footer
              appState={appState}
              actionManager={actionManager}
              showExitZenModeBtn={showExitZenModeBtn}
              renderWelcomeScreen={renderWelcomeScreen}
              onHome={onHome}
            />
            {appState.scrolledOutside && (
              <button
                type="button"
                className="scroll-back-to-content"
                onClick={() => {
                  setAppState((appState) => ({
                    ...calculateScrollCenter(elements, appState),
                  }));
                }}
              >
                {t("buttons.scrollBackToContent")}
              </button>
            )}
          </div>
          {renderSidebars()}
        </>
      )}
    </>
  );

  return (
    <UIAppStateContext.Provider value={appState}>
      <TunnelsJotaiProvider>
        <TunnelsContext.Provider value={tunnels}>
          {layerUIJSX}
        </TunnelsContext.Provider>
      </TunnelsJotaiProvider>
    </UIAppStateContext.Provider>
  );
};

export default LayerUI;
